import { Box, Card, Grid, Typography } from "@mui/material";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { appUrl, kickClientId } from "../../common/api/config";
import kickLogo from "../../common/assets/img/platform_logos/kick.svg";
import Button from "../../common/components/Button";
import { SPACING } from "../../common/constants/spacing";
import { requestAccessTokenFromKick } from "../api/kick";

// Generate PKCE code verifier and challenge
const generateCodeVerifier = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode.apply(null, Array.from(array)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
};

const generateCodeChallenge = async (verifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(hash))))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
};

const openKickOAuthPopup = async () => {
  const redirectUri = `${appUrl}/kick-oauth-callback`;

  // Generate PKCE parameters
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  // Store code verifier for later use in token exchange
  sessionStorage.setItem("kick_code_verifier", codeVerifier);

  // Scopes: "Read user information (including email address)" and "Read channel information"
  const scope = "user:read:email channel:read";
  const state = Math.random().toString(36).substring(7);

  // Kick OAuth server is at id.kick.com
  const oauthUrl = `https://id.kick.com/oauth/authorize?response_type=code&client_id=${kickClientId}&redirect_uri=${encodeURIComponent(
    redirectUri,
  )}&scope=${encodeURIComponent(scope)}&code_challenge=${codeChallenge}&code_challenge_method=S256&state=${state}`;

  const width = 500;
  const height = 600;
  const left = (window.innerWidth - width) / 2;
  const top = (window.innerHeight - height) / 2;

  window.open(
    oauthUrl,
    "KickOAuth",
    `width=${width},height=${height},top=${top},left=${left}`,
  );
};

export const LinkKickCard = ({ createSource }) => {
  const { t } = useTranslation("sources");

  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.origin !== window.location.origin) return;

      if (event.data?.source === "kick-oauth" && event.data.code) {
        const authCode = event.data.code;
        const codeVerifier = sessionStorage.getItem("kick_code_verifier");

        if (!codeVerifier) {
          console.error("Code verifier not found in session storage");
          return;
        }

        const tokenData = await requestAccessTokenFromKick(
          authCode,
          codeVerifier,
        );
        sessionStorage.removeItem("kick_code_verifier"); // Clean up

        const expiresAt = Date.now() + tokenData.expires_in * 1000;
        const source = {
          account_name: "Kick Account",
          platform: "kick",
          access_token: tokenData.access_token,
          refresh_token: tokenData.refresh_token,
          token_expires_at: new Date(expiresAt).toISOString(),
        };
        try {
          await createSource(source);
        } catch (error) {
          console.error("Error creating data source:", error);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid size={{ xs: 12, md: 4 }}>
      <Card variant="outlined" sx={{ p: 3, height: "100%", mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          <Box>
            <img
              src={kickLogo}
              alt="Kick Logo"
              style={{ height: "70px", objectFit: "contain", marginBottom: 10 }}
            />
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", mb: SPACING.xs }}
            >
              Kick
            </Typography>
            <Typography variant="bodySm" sx={{ color: "text.secondary" }}>
              {t("platforms.kick.description")}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            sx={{ mt: 3 }}
            onClick={openKickOAuthPopup}
            fullWidth
          >
            {t("platforms.kick.buttonText")}
          </Button>
        </Box>
      </Card>
    </Grid>
  );
};
